import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import Paginate from '../../components/Paginate';
import { Link } from 'react-router-dom';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import useBuildingService from '../../hooks/useBuildingService';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Typography
} from "@mui/material";
import { formatDate, itemsPerPage, handleApiError } from '../../components/Utils';


function ListBuildings() {
    const { getBuildings } = useBuildingService();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchReset, setFetchReset] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [buildings, setBuildings] = useState([]);

    const [name, setName] = usePersistedState('list_buildings_name', null);
    const [minBudget, setMinBudget] = usePersistedState('list_buildings_minBudget', null);
    const [maxBudget, setMaxBudget] = usePersistedState('list_buildings_maxBudget', null);
    const [code, setCode] = usePersistedState('list_buildings_code', null);
    const [status, setStatus] = useState(null);

    const debouncedName = useDebounce(name);
    const debouncedMinBudget = useDebounce(minBudget);
    const debouncedMaxBudget = useDebounce(maxBudget);
    const debouncedCode = useDebounce(code);

    const [sortColumn, setSortColumn] = usePersistedState('list_buildings_sortColumn', '');
    const [sortDirection, setSortDirection] = usePersistedState('list_buildings_sortDirection', 'ASC');

    const isFirstFetch = useRef(true);

    const fetchBuildings = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            setError(null);
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;
            if (!fetchReset) {
                if (name) params.name = name;
                if (code) params.code = code;
                if (status) params.building_status = status;
                if (minBudget) params.min_budget = minBudget;
                if (maxBudget) params.max_budget = maxBudget;
                if (sortColumn) params.sort_by = sortColumn;
                if (sortDirection) params.sort_direction = sortDirection;
            } else {
                setFetchReset(false);
            }

            const result = await getBuildings(params);

            setBuildings(result.data.items);
            setTotalItems(result.data.total);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al obtener obras.'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, fetchReset, code, name, minBudget, maxBudget, status, sortColumn, sortDirection, getBuildings]);

    useEffect(() => {
        fetchBuildings();
    }, [currentPage, sortColumn, sortDirection, fetchReset, status, debouncedName, debouncedCode, debouncedMinBudget, debouncedMaxBudget]);

    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setCurrentPage(1);
        setCode(null);
        setName(null);
        setStatus(null);
        setMinBudget(null);
        setMaxBudget(null);
        setFetchReset(true);
    };

    const cellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '75px'
    };

    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        name="code"
                                        value={code || ""}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        name="name"
                                        value={name || ""}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Presupuesto min"
                                        name="minBudget"
                                        value={minBudget || ""}
                                        onChange={(e) => setMinBudget(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Presupuesto max"
                                        name="maxBudget"
                                        value={maxBudget || ""}
                                        onChange={(e) => setMaxBudget(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Estado</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            name="status"
                                            value={status || ""}
                                            onChange={handleStatusChange}
                                            label="Tipo"
                                        >
                                            <MenuItem value="not_started">NO EMPEZADA</MenuItem>
                                            <MenuItem value="in_course">EN CURSO</MenuItem>
                                            <MenuItem value="finished" >TERMINADA</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                        Limpiar Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <br />
                    <br />
                    <br />
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                Nombre {sortColumn === 'name' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('budget')} style={{ cursor: 'pointer' }}>
                                                Presupuesto {sortColumn === 'budget' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>
                                                Fecha inicio {sortColumn === 'start_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('end_date')} style={{ cursor: 'pointer' }}>
                                                Fecha final {sortColumn === 'end_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Estado</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {buildings.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            <Link to={`/buildings/update/${item.id}`}>
                                                {item.code|| '-'}
                                            </Link>
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.name || '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.budget ? item.budget.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {formatDate(item.start_date)|| '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {formatDate(item.end_date)|| '-'}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {item.building_status === 'not_started' ? 'NO EMPEZADA' :
                                                item.building_status === 'in_course' ? 'EN CURSO' :
                                                    item.building_status === 'finished' ? 'TERMINADA' :
                                                        item.offer_status}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>

            )}
            <br />
            <br />
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    )
}
export default ListBuildings;
